import React, { useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Divider,
  MenuItem,
  Typography,
  Avatar,
  IconButton,
  Stack,
  Tooltip,
  Modal,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import Iconify from '../../components/Iconify';
import MenuPopover from '../../components/MenuPopover';
import { useAuthContext } from '../../context/AuthContext';
import UserHistoryModal from './UserHistoryModal';
import ProfilePage from './ProfilePage';

const formatUserName = (name) => {
  if (!name) return '';
  const parts = name.trim().split(' ');
  return parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1]}` : name;
};

export default function AccountPopover() {
  const navigate = useNavigate();
  const {
    cliente,
    logout: contextLogout,
    updateUserProfile,
    updateUserPassword,
  } = useAuthContext();

  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [openHistory, setOpenHistory] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [openProfile, setOpenProfile] = useState(false);

  const isAdmin = cliente?.isAdmin ?? false;

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleLogout = useCallback(async () => {
    try {
      await contextLogout?.();
      navigate('/app/home', { replace: true });
    } catch (error) {
      console.error('Failed to trigger logout:', error);
    }
  }, [contextLogout, navigate]);

  const displayName = formatUserName(cliente?.nome) || 'Usuário';
  const displayEmail = cliente?.email || 'Sem email';
  const displayAvatar = cliente?.photoURL || cliente?.avatar || '';

  const handleCloseProfile = () => setOpenProfile(false);

  const handleUpdateProfile = async (updatedUser) => {
    if (!updateUserProfile) return;
    console.log('handleUpdateProfile - updatedUser:', updatedUser);
    if (!updatedUser?.id) {
      console.error('handleUpdateProfile: ID do usuário não fornecido');
      return;
    }
    await updateUserProfile(updatedUser);
  };

  const handleUpdatePassword = async (newPassword) => {
    if (!updateUserPassword) return;
    console.log('handleUpdatePassword - newPassword:', newPassword);
    await updateUserPassword(newPassword);
  };

  const handleOpenProfile = () => {
    if (!cliente || !cliente.id) {
      console.error(
        'Não é possível abrir ProfilePage: usuário não autenticado ou ID ausente'
      );
      return;
    }
    setOpenProfile(true);
    handleClose();
  };

  return (
    <>
      <Stack direction="row" spacing={1} alignItems="center">
        <Tooltip title="Opções da conta">
          <IconButton
            ref={anchorRef}
            onClick={handleOpen}
            sx={{
              p: 0,
              width: 50,
              height: 50,
              ...(open && {
                bgcolor: (theme) => alpha(theme.palette.grey[500], 0.16),
              }),
            }}
          >
            <Avatar
              src={displayAvatar}
              alt={displayName}
              sx={{ width: 44, height: 44 }}
            />
          </IconButton>
        </Tooltip>
      </Stack>

      <MenuPopover
        open={open}
        onClose={handleClose}
        anchorEl={anchorRef.current}
        sx={{
          p: 0,
          mt: 1.5,
          ml: 0.75,
          width: 310,
          borderRadius: 3,
          boxShadow: 3,
          '& .MuiMenuItem-root': {
            typography: 'body2',
            borderRadius: 30,
          },
        }}
      >
        <Box sx={{ my: 2, px: 3, textAlign: 'center' }}>
          <Avatar
            src={displayAvatar}
            alt={displayName}
            sx={{ width: 72, height: 72, mx: 'auto', mb: 2 }}
          />
          <Typography variant="h6" noWrap>
            {displayName}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {displayEmail}
          </Typography>
        </Box>

        <Divider sx={{ borderStyle: 'solid', borderColor: 'divider' }} />

        <Stack spacing={2} sx={{ p: 2 }}>
          {/* Home visível para TODO mundo (admin ou não) */}
          <MenuItem
            key="home"
            onClick={() => {
              navigate('/app/home');
              handleClose();
            }}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Iconify icon="eva:home-outline" sx={{ mr: 2, width: 22, height: 22 }} />
            Início
          </MenuItem>

          <MenuItem
            key="profile"
            onClick={handleOpenProfile}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Iconify
              icon="eva:person-outline"
              sx={{ mr: 2, width: 22, height: 22 }}
            />
            Meu Perfil
          </MenuItem>

          {isAdmin && (
            <>
              <MenuItem
                key="manage-users"
                onClick={() => {
                  navigate('/app/users');
                  handleClose();
                }}
              >
                <Iconify
                  icon="eva:people-fill"
                  sx={{ mr: 2, width: 22, height: 22 }}
                />
                Gerenciar Usuários
              </MenuItem>
              <MenuItem
                key="dashboard"
                onClick={() => {
                  navigate('/app/dashboard');
                  handleClose();
                }}
              >
                <Iconify
                  icon="material-symbols:dashboard"
                  sx={{ mr: 2, width: 22, height: 22 }}
                />
                Dashboard
              </MenuItem>
            </>
          )}

          <MenuItem key="logout" onClick={handleLogout} sx={{ color: 'error.main' }}>
            <Iconify icon="eva:log-out-fill" sx={{ mr: 2, width: 22, height: 22 }} />
            Sair
          </MenuItem>
        </Stack>
      </MenuPopover>

      <Modal
        open={openProfile}
        onClose={handleCloseProfile}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
        }}
      >
        <Box sx={{ width: '90vw', maxWidth: 1400 }}>
          {cliente && cliente.id ? (
            <ProfilePage
              user={cliente}
              onBack={handleCloseProfile}
              onUpdateProfile={handleUpdateProfile}
              onUpdatePassword={handleUpdatePassword}
            />
          ) : (
            <Typography color="error">
              Erro: Usuário não autenticado ou ID não disponível.
            </Typography>
          )}
        </Box>
      </Modal>

      <UserHistoryModal
        open={openHistory}
        onClose={() => setOpenHistory(false)}
        user={selectedUser}
        isAdmin={isAdmin}
      />
    </>
  );
}
